import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SendInvoiceDialog from "@/components/SendInvoiceDialog";
import InvoicePreview from "@/components/InvoicePreview";
import JobInvoicePreview from "@/components/JobInvoicePreview";
import DayProfitBreakdown, { AwaitingSignatureBanner } from "@/components/DayProfitBreakdown";
import AdminEditJob from "@/components/AdminEditJob";
import EditDayDialog from "@/components/EditDayDialog";
import { ClipboardCheck, ArrowRight, Building2, MapPin, Layers, CalendarRange, ChevronDown, ChevronRight, FileText, FileSignature } from "lucide-react";


export default function WaitingToInvoice() {
  const [jobs, setJobs] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [sendInv, setSendInv] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [editDay, setEditDay] = useState(null);
  const [previewRefresh, setPreviewRefresh] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [invoiceJob, setInvoiceJob] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState({});
  const [combineDate, setCombineDate] = useState("");
  const [combineCtx, setCombineCtx] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/waiting-to-invoice").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);

  const normAddr = (s) => (s || "").trim().toLowerCase();
  const invoiceable = (j) => !(j.pending_days > 0);  // ready jobs only (no pending days)
  const addrCounts = {};
  jobs.forEach((j) => { if (invoiceable(j)) { const k = normAddr(j.site_address); if (k) addrCounts[k] = (addrCounts[k] || 0) + 1; } });
  const selectedList = jobs.filter((j) => selected[j.id]);
  const selectedAddrs = [...new Set(selectedList.map((j) => normAddr(j.site_address)))];
  const sameAddr = selectedAddrs.length <= 1;
  const canCombine = selectedList.length >= 2 && sameAddr;
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // Combine several same-address jobs into ONE invoice (each line keeps its own work date / month).
  const openCombinedPreview = async () => {
    if (selectedList.length < 2) return;
    const ids = selectedList.map((j) => j.id);
    const project_id = selectedList[0].project_id;
    const week_start = selectedList[0].appointment_date;
    setCombineCtx({ ids, project_id, week_start, date: combineDate });
    try {
      const r = await api.post("/invoices/preview", { project_id, week_start, docket_ids: ids, invoice_notes: "", invoice_date: combineDate || undefined });
      setInvoiceJob(null); setPreview(r.data); setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Preview the formatted invoice for a single job (same flow as Weekly Billing).
  const openPreview = async (j) => {
    setBusyId(j.id);
    try {
      const r = await api.post("/invoices/preview", { project_id: j.project_id, week_start: j.appointment_date, docket_ids: [j.id], invoice_notes: "" });
      setInvoiceJob(j); setPreview(r.data); setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  // Create the draft invoice, then open the existing Send / Sync confirmation.
  const createInvoice = async () => {
    setCreating(true);
    try {
      let payload;
      if (combineCtx) {
        payload = { project_id: combineCtx.project_id, week_start: combineCtx.week_start, docket_ids: combineCtx.ids, invoice_notes: "", invoice_date: combineCtx.date || undefined };
      } else {
        if (!invoiceJob) return;
        payload = { project_id: invoiceJob.project_id, week_start: invoiceJob.appointment_date, docket_ids: [invoiceJob.id], invoice_notes: "" };
      }
      const r = await api.post("/invoices", payload);
      toast.success(`Draft invoice ${r.data.invoice_number} ready — review & send`);
      setPreviewOpen(false); setExpandedId(null); setInvoiceJob(null);
      setCombineCtx(null); setSelected({}); setCombineDate("");
      await load();
      setSendInv(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const toWeekly = async (j) => {
    setBusyId(j.id);
    try {
      await api.patch(`/jobs/${j.id}/move-to-weekly-billing`, { billing_notes: null });
      toast.success(`${j.job_number} moved to Ready to Bill`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const combinedDocket = async (j) => {
    setBusyId(j.id);
    try {
      const res = await api.get(`/jobs/${j.id}/combined-docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${j.job_number || "combined"}-combined.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate combined docket"); }
    finally { setBusyId(""); }
  };



  return (
    <div className="space-y-6 animate-fade-up" data-testid="waiting-to-invoice-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><ClipboardCheck className="w-8 h-8" />Waiting to Invoice</h1>
        <p className="text-slate-500 mt-1">Expand a job to review the completed docket, charge codes &amp; profitability, then Invoice &amp; Send. Multi-day jobs group under Ready to Bill.</p>
      </div>

      {jobs.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center shadow-sm">
          <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400">No dockets waiting for review. Signed jobs will appear here for approval.</p>
        </div>
      )}

      <div className="space-y-4">
        {jobs.map((j) => {
          const isOpen = expandedId === j.id;
          return (
            <div key={j.id} data-testid={`wti-job-${j.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                {invoiceable(j) && addrCounts[normAddr(j.site_address)] > 1 && (
                  <label data-testid={`wti-select-${j.id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 shrink-0 cursor-pointer pt-0.5">
                    <input type="checkbox" checked={!!selected[j.id]} onChange={() => toggleSelect(j.id)}
                      className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy" />
                  </label>
                )}
                <button data-testid={`wti-toggle-${j.id}`} onClick={() => setExpandedId(isOpen ? null : j.id)} className="flex-1 min-w-0 text-left flex items-start gap-3 group">
                  {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> : <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <p className="font-semibold text-navy group-hover:underline">{j.customer_name} <span className="text-slate-400 font-normal text-sm">· {j.job_number}</span></p>
                    <p className="text-sm text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                      <span>{formatDate(j.appointment_date)}</span>
                      {j.site_address && <span className="flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5" />{j.site_address}</span>}
                    </p>
                    {j.builder && <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5"><Building2 className="w-3.5 h-3.5" />{j.builder}</p>}
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full mt-2 ${j.multi_day ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {j.multi_day ? <><Layers className="w-3 h-3" />Multi-day ({j.day_count || j.project_job_count} days)</> : <><CalendarRange className="w-3 h-3" />Single-day</>}
                    </span>
                    {invoiceable(j) && addrCounts[normAddr(j.site_address)] > 1 && (
                      <span data-testid={`wti-same-address-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full mt-2 ml-2 bg-teal-100 text-teal-700">
                        <MapPin className="w-3 h-3" />{addrCounts[normAddr(j.site_address)]} jobs at this address · tick to combine
                      </span>
                    )}
                    {(j.awaiting_signature_days || []).length > 0 && (
                      <span data-testid={`wti-with-client-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full mt-2 ml-2 bg-orange-100 text-orange-800">
                        <FileSignature className="w-3 h-3" />With client to sign
                      </span>
                    )}
                    {j.pending_days > 0 && (
                      <span data-testid={`wti-pending-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full mt-2 ml-2 bg-amber-100 text-amber-800">
                        <CalendarRange className="w-3 h-3" />Waiting on {j.pending_days} more day{j.pending_days > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </button>
                <div className="flex gap-2 shrink-0">
                  {j.multi_day && j.day_count > 1 && (
                    <Button variant="outline" data-testid={`wti-combined-docket-${j.id}`} disabled={busyId === j.id} onClick={() => combinedDocket(j)}
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                      <FileText className="w-4 h-4 mr-1.5" />Combined Docket
                    </Button>
                  )}
                  {j.pending_days > 0 ? (
                    <Button variant="outline" data-testid={`wti-review-${j.id}`} onClick={() => setExpandedId(isOpen ? null : j.id)}>
                      {isOpen ? "Hide review" : "Review completed days"}
                    </Button>
                  ) : j.multi_day ? (
                    <Button data-testid={`wti-to-weekly-${j.id}`} disabled={busyId === j.id} onClick={() => toWeekly(j)} className="bg-indigo-600 hover:bg-indigo-700">
                      <Layers className="w-4 h-4 mr-2" />Move to Ready to Bill
                    </Button>
                  ) : (
                    <Button variant="outline" data-testid={`wti-preview-${j.id}`} onClick={() => setExpandedId(isOpen ? null : j.id)}>
                      {isOpen ? "Hide review" : "Review job & invoice"}
                    </Button>
                  )}
                  <Button variant="outline" data-testid={`wti-open-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}>
                    Open Full Job <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              <AwaitingSignatureBanner days={j.awaiting_signature_days || []} jobId={j.id} />

              <JobInvoicePreview
                jobId={j.id}
                open={isOpen && !j.multi_day}
                onOpenFull={() => navigate(`/jobs/${j.id}`)}
                onEditInvoice={(fullJob) => setEditJob(fullJob)}
                onInvoiceSend={j.multi_day || !invoiceable(j) ? undefined : () => openPreview(j)}
                sending={busyId === j.id}
                refreshKey={previewRefresh}
              />

              {isOpen && j.multi_day && (
                <div data-testid={`wti-days-${j.id}`} className="border-t border-slate-100 p-4 bg-slate-50/40">
                  <DayProfitBreakdown days={j.days || []} daysTotal={j.days_total} jobId={j.id}
                    pendingDays={j.pending_days} pendingDates={j.pending_dates}
                    onEditDay={(day) => setEditDay({ jobId: j.id, index: day.index })} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SendInvoiceDialog inv={sendInv} open={!!sendInv} resendMode={false}
        onOpenChange={(o) => { if (!o) setSendInv(null); }}
        onDone={() => { setSendInv(null); load(); }} />

      {selectedList.length > 0 && (
        <div data-testid="combine-bar" className="fixed left-1/2 -translate-x-1/2 bottom-24 sm:bottom-6 z-40 w-[94%] max-w-3xl bg-navy text-white rounded-2xl shadow-2xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{selectedList.length} job{selectedList.length > 1 ? "s" : ""} selected{selectedList.length >= 2 && sameAddr ? " · same address" : ""}</p>
              {!sameAddr ? (
                <p className="text-[11px] text-amber-300 mt-0.5">Pick jobs at the <b>same address</b> to combine them.</p>
              ) : (
                <p className="text-[11px] text-slate-300 mt-0.5">Each line keeps its own work date &amp; month; this bill combines them into one invoice.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-300 mb-0.5">Invoice date (optional)</span>
                <input type="date" data-testid="combine-invoice-date" value={combineDate} onChange={(e) => setCombineDate(e.target.value)}
                  className="rounded-lg text-navy text-sm px-2 py-1.5" />
              </div>
              <Button variant="outline" data-testid="combine-clear" onClick={() => { setSelected({}); setCombineDate(""); }}
                className="bg-transparent border-white/30 text-white hover:bg-white/10">Clear</Button>
              <Button data-testid="combine-create" disabled={!canCombine} onClick={openCombinedPreview}
                className="bg-white text-navy hover:bg-slate-100 disabled:opacity-40">
                <Layers className="w-4 h-4 mr-1.5" />Combine into one invoice
              </Button>
            </div>
          </div>
        </div>
      )}

      <InvoicePreview open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o) { setInvoiceJob(null); setCombineCtx(null); } }}
        preview={preview} invoiceNumber={null} onCreate={createInvoice} creating={creating}
        ctaLabel="Send Invoice & Sync" creatingLabel="Creating…"
        onDownloadPdf={async () => {
          const payload = combineCtx
            ? { project_id: combineCtx.project_id, week_start: combineCtx.week_start, docket_ids: combineCtx.ids, invoice_notes: "", invoice_date: combineCtx.date || undefined }
            : (invoiceJob ? { project_id: invoiceJob.project_id, week_start: invoiceJob.appointment_date, docket_ids: [invoiceJob.id], invoice_notes: "" } : null);
          if (!payload) return;
          try {
            const res = await api.post("/invoices/preview-pdf", payload, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = "invoice-preview.pdf";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          } catch (e) { toast.error("Could not generate invoice PDF"); }
        }} />

      <AdminEditJob open={!!editJob} setOpen={(o) => { if (!o) setEditJob(null); }} job={editJob}
        onSaved={() => { setEditJob(null); setPreviewRefresh((n) => n + 1); load(); }} />

      <EditDayDialog open={!!editDay} setOpen={(o) => { if (!o) setEditDay(null); }}
        jobId={editDay?.jobId} index={editDay?.index}
        onSaved={() => { setEditDay(null); setPreviewRefresh((n) => n + 1); load(); }} />
    </div>
  );
}
